import React, { useEffect } from "react";
import { connect, useSelector } from "react-redux";
import { Field, getFormValues, reduxForm } from "redux-form";
import { Toggle } from "../../Form";
import Loading from "../../Loading";
import { ButtonPrimary } from "../../Button";
import RolesSelect from "../../Form/RolesForm/RolesSelect";
import {
  isRoleEdgeAdmin,
  isRolePlatformAdmin,
} from "../../../modules/permissions";
import {
  userAgencyFeaturesObject,
  userAgencyObject,
} from "../../../modules/auth";
import { AGENCY_PERMISSIONS } from "../../../constants";

const FeaturesSection = ({ title, children }) => (
  <div className="flex flex-col">
    <h4 className="my-4">{title}</h4>
    <div className="flex flex-wrap">{children}</div>
  </div>
);

const parentHasFeature = (features, permissionKey) => {
  return !!(features && features[permissionKey]);
};

function AgencyFeaturesForm({
  agency,
  agenciesForSelect,
  handleSubmit,
  onSubmit,
  submitting,
  userAgency,
  userAgencyFeatures,
  isPlatformAdmin,
  form,
  change,
  parentFeatures,
}) {
  const values = useSelector(getFormValues(form));
  const isSuperAgency = values?.is_super_agent;

  const reportDependentPermissions = [
    AGENCY_PERMISSIONS.CAN_VIEW_SPEND_SNAPSHOT,
    AGENCY_PERMISSIONS.CAN_VIEW_ENQUIRY_LOG,
    AGENCY_PERMISSIONS.CAN_VIEW_SPEND_REPORT,
    AGENCY_PERMISSIONS.CAN_VIEW_CASHFLOW,
    AGENCY_PERMISSIONS.CAN_VIEW_ANNUAL,
    AGENCY_PERMISSIONS.CAN_VIEW_AGENCY_DASHBOARD_SNAPSHOT,
  ];

  // if agency can't access report, disable them all
  useEffect(() => {
    if (!values[AGENCY_PERMISSIONS.CAN_ACCESS_REPORTS]) {
      reportDependentPermissions.forEach(permission => {
        change(permission, 0);
      });
    }
  }, [values[AGENCY_PERMISSIONS.CAN_ACCESS_REPORTS]]);

  const agenciesOptions = Array.isArray(agenciesForSelect)
    ? agenciesForSelect
        .sort((a, b) => a.name.localeCompare(b.name))
        .map(item => ({
          label: item.name,
          value: item.id,
        }))
    : [];

  const isSuperAgent = Boolean(userAgency?.is_super_agent);
  const canHaveCorporate = Boolean(
    userAgencyFeatures?.can_have_corporate_accounts
  );
  const canManageInvoicing = Boolean(userAgencyFeatures?.can_manage_invoicing);
  const canManageLegalText = Boolean(
    userAgencyFeatures?.can_manage_specific_company_legal_text
  );
  const isEditingChild = Boolean(
    agency?.parent_agency_id && agency.parent_agency_id > 0
  );
  const isEditingOwnAgency = agency?.id === userAgency?.id;
  const isTopLevelAgency = agency?.parent_agency_id == null;
  const canExportToXeroVisible =
    parseInt(values?.can_manage_invoicing) === 1 && canManageInvoicing;

  const agencySetup = [
    {
      description:
        "Platform admin always has access, OR a super agent editing a child agency where the parent agency allows branded emails",
      name: AGENCY_PERMISSIONS.CAN_HAVE_BRANDED_EMAILS,
      label: "Can have branded emails?",
      visible:
        isPlatformAdmin ||
        (isSuperAgent &&
          isEditingChild &&
          parentHasFeature(
            parentFeatures,
            AGENCY_PERMISSIONS.CAN_HAVE_BRANDED_EMAILS
          )),
      // visible: (isEditingChild && (isPlatformAdmin || isSuperAgent)) || (!isEditingChild && isPlatformAdmin),
    },
    {
      description:
        "Platform admin always has access, OR a super agent editing a child agency where the parent agency allows managing booking desks",
      name: AGENCY_PERMISSIONS.CAN_MANAGE_BOOKING_DESKS,
      label: "Can Manage Booking Desks?",
      visible:
        isPlatformAdmin ||
        (isEditingChild &&
          isSuperAgent &&
          parentHasFeature(
            parentFeatures,
            AGENCY_PERMISSIONS.CAN_MANAGE_BOOKING_DESKS
          )),
    },
    {
      description:
        "Platform admin always has access, OR a super agent editing a child agency where the parent agency allows managing business units",
      name: AGENCY_PERMISSIONS.CAN_MANAGE_BUSINESS_UNITS,
      label: "Can Manage Business Units?",
      visible:
        isPlatformAdmin ||
        (isEditingChild &&
          isSuperAgent &&
          parentHasFeature(
            parentFeatures,
            AGENCY_PERMISSIONS.CAN_MANAGE_BUSINESS_UNITS
          )),
      // visible: (isEditingChild && (isPlatformAdmin || isSuperAgent)) || (!isEditingChild && isPlatformAdmin),
    },
    {
      description:
        "Platform admin always has access, OR a super agent editing a child agency where the parent agency allows managing corporate account",
      name: AGENCY_PERMISSIONS.CAN_HAVE_CORPORATE_ACCOUNTS,
      label: "Can Manage Corporate Accounts?",
      visible:
        isPlatformAdmin || (isSuperAgent && canHaveCorporate && isEditingChild),
    },
    {
      description:
        "Platform admin always has access, OR a super agent editing a child agency/company where the parent agency allows preferred programmes",
      name: AGENCY_PERMISSIONS.CAN_MANAGE_PREFERRED_PROGRAMME,
      label: "Can Manage Preferred Programme?",
      visible:
        isPlatformAdmin || (isSuperAgent && canHaveCorporate && isEditingChild),
    },
    {
      description:
        "Platform admin always has access, OR a super agent editing a child agency/company where the parent agency allows deals",
      name: AGENCY_PERMISSIONS.CAN_MANAGE_COMMERCIAL_DEALS,
      label: "Can Manage Commercial Deals?",
      visible:
        isPlatformAdmin || (isSuperAgent && canHaveCorporate && isEditingChild),
    },
  ];

  const reportsAccess = [
    {
      description:
        "Platform admin always has access, OR super agent can only enable child agencies",
      name: AGENCY_PERMISSIONS.CAN_ACCESS_REPORTS,
      label: "Can access reports?",
      visible:
        isPlatformAdmin ||
        (isSuperAgent &&
          isEditingChild &&
          parentHasFeature(
            parentFeatures,
            AGENCY_PERMISSIONS.CAN_ACCESS_REPORTS
          )),
    },
    {
      description:
        "Platform admin always has access, OR super agent can only enable for child agencies",
      name: AGENCY_PERMISSIONS.CAN_VIEW_SPEND_SNAPSHOT,
      label: "Can view spend snapshot?",
      visible:
        isPlatformAdmin ||
        (isSuperAgent &&
          isEditingChild &&
          parentHasFeature(
            parentFeatures,
            AGENCY_PERMISSIONS.CAN_VIEW_SPEND_SNAPSHOT
          )),
    },
    {
      description:
        "Platform admin always has access, OR super agent can only enable for child agencies",
      name: AGENCY_PERMISSIONS.CAN_VIEW_ENQUIRY_LOG,
      label: "Can view enquiry log?",
      visible:
        isPlatformAdmin ||
        (isSuperAgent &&
          isEditingChild &&
          parentHasFeature(
            parentFeatures,
            AGENCY_PERMISSIONS.CAN_VIEW_ENQUIRY_LOG
          )),
    },
    {
      description:
        "Platform admin always has access, OR super agent can only enable for child agencies",
      name: AGENCY_PERMISSIONS.CAN_VIEW_SPEND_REPORT,
      label: "Can view spend report?",
      visible:
        isPlatformAdmin ||
        (isSuperAgent &&
          isEditingChild &&
          parentHasFeature(
            parentFeatures,
            AGENCY_PERMISSIONS.CAN_VIEW_SPEND_REPORT
          )),
    },
    {
      description:
        "Platform admin always has access, OR super agent can only enable child agencies and parent has feature enabled",
      name: AGENCY_PERMISSIONS.CAN_VIEW_CASHFLOW,
      label: "Can view cash flow?",
      visible:
        isPlatformAdmin ||
        (isSuperAgent &&
          isEditingChild &&
          parentHasFeature(
            parentFeatures,
            AGENCY_PERMISSIONS.CAN_VIEW_CASHFLOW
          )),
    },
    {
      description:
        "Platform admin always has access, OR super agent can only enable child agencies and parent has feature enabled",
      name: AGENCY_PERMISSIONS.CAN_VIEW_ANNUAL,
      label: "Can view yearly report?",
      visible:
        isPlatformAdmin ||
        (isSuperAgent &&
          isEditingChild &&
          parentHasFeature(parentFeatures, AGENCY_PERMISSIONS.CAN_VIEW_ANNUAL)),
    },
    {
      description:
        "Platform admin always has access, OR super agent can only enable child agencies and parent has feature enabled",
      name: AGENCY_PERMISSIONS.CAN_VIEW_AGENCY_DASHBOARD_SNAPSHOT,
      label: "Can view snapshot report on dashboard?",
      visible:
        isPlatformAdmin ||
        (isSuperAgent &&
          isEditingChild &&
          parentHasFeature(
            parentFeatures,
            AGENCY_PERMISSIONS.CAN_VIEW_AGENCY_DASHBOARD_SNAPSHOT
          )),
    },
    {
      description:
        "Platform admin always has access, OR super agent can only enable child agencies and parent has feature enabled",
      name: AGENCY_PERMISSIONS.CAN_VIEW_PREFERRED_PROGRAMME_SPEND,
      label: "Can view preferred programme spend report?",
      visible:
        isPlatformAdmin ||
        (isSuperAgent &&
          isEditingChild &&
          parentHasFeature(
            parentFeatures,
            AGENCY_PERMISSIONS.CAN_VIEW_PREFERRED_PROGRAMME_SPEND
          )),
    },
  ];

  const superAgencyFeatures = [
    {
      description:
        "If child agency, Platform admin always has access, OR super agency is not editing own agency",
      name: AGENCY_PERMISSIONS.IS_MANAGED_BY_PARENT_AGENCY,
      label: "Is managed by parent agency?",
      visible:
        isEditingChild &&
        (isPlatformAdmin || (isSuperAgent && !isEditingOwnAgency)),
    },
    {
      description: "Parent Agency, Platform admin always has access",
      name: AGENCY_PERMISSIONS.IS_SUPER_AGENT,
      label: "Is Super Agency - can have child agencies?",
      visible: isTopLevelAgency && isPlatformAdmin,
    },
    {
      description: "Parent agency and super agency, only by platform admin",
      name: AGENCY_PERMISSIONS.CAN_ADD_CHILD_AGENCIES,
      label: "Can add own child agencies?",
      visible: isPlatformAdmin && isSuperAgency && isTopLevelAgency,
    },
    {
      name: "child_agencies",
      label: "Child Agencies",
      component: RolesSelect,
      visible: isPlatformAdmin && isSuperAgency && isTopLevelAgency,
      options: agenciesOptions,
    },
  ];

  const rfpFeatures = [
    {
      name: AGENCY_PERMISSIONS.CAN_SUPPRESS_BRIEF_NOTIFICATIONS,
      label: "Can Suppress Brief Notifications?",
      visible: isPlatformAdmin,
    },
    {
      description:
        "Platform admin can see, super agents can only see if editing child and has their agency has permission",
      name: AGENCY_PERMISSIONS.CAN_MANAGE_SPECIFIC_COMPANY_LEGAL_TEXT,
      label: "Can Manage Specific Company Legal Texts?",
      visible:
        isPlatformAdmin ||
        (isEditingChild && isSuperAgent && canManageLegalText),
    },
    {
      description:
        "Platform admin can see, super agents can only see if editing child and has their agency has permission",
      name: AGENCY_PERMISSIONS.CAN_MANAGE_INVOICING,
      label: "Can Manage Invoicing?",
      visible:
        isPlatformAdmin ||
        (isEditingChild && isSuperAgent && canManageInvoicing),
    },
    {
      description:
        "Platform admin can see, super agents can only see if editing child and has their agency has permission to manage invoicing",
      name: AGENCY_PERMISSIONS.CAN_EXPORT_TO_XERO,
      label: "Can Export To Xero?",
      visible:
        isPlatformAdmin ||
        (isEditingChild && isSuperAgent && canExportToXeroVisible),
    },
    {
      description:
        "Platform admin can see, super agents can only see if editing child and has their agency has permission",
      name: AGENCY_PERMISSIONS.CAN_VIEW_AGENTS_CALENDAR,
      label: "Can view agents calendar?",
      visible:
        isPlatformAdmin ||
        (isEditingChild &&
          isSuperAgent &&
          parentHasFeature(
            parentFeatures,
            AGENCY_PERMISSIONS.CAN_VIEW_AGENTS_CALENDAR
          )),
    },
  ];

  if (isSuperAgent && !isEditingChild) {
    return (
      <>
        Your agency cannot manage these settings - please contact the EDGE team
        to find out more.
      </>
    );
  }

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <FeaturesSection title="Agency Setup">
        {agencySetup.map((item, index) => {
          if (!item.visible) return null;
          return (
            <div
              key={`${index}_agency_setup`}
              className="col col-w-full md:col-w-1/3"
            >
              <Field name={item.name} label={item.label} component={Toggle} />
            </div>
          );
        })}
      </FeaturesSection>

      <hr />

      {isPlatformAdmin || isSuperAgent ? (
        <>
          <FeaturesSection title="Super Agency">
            {superAgencyFeatures.map((item, index) => {
              if (!item.visible) return null;

              return (
                <div
                  key={`${index}_super_agency`}
                  className="col col-w-full md:col-w-1/3"
                >
                  {item.component ? (
                    <Field
                      name={item.name}
                      label={item.label}
                      component={item.component}
                      options={item.options}
                    />
                  ) : (
                    <Field
                      name={item.name}
                      label={item.label}
                      component={Toggle}
                    />
                  )}
                </div>
              );
            })}

            {agency.id !== userAgency.id && !isTopLevelAgency && (
              <div className="col col-w-full">
                <p>
                  This agency is a child of another agency and cannot be a super
                  agent
                </p>
              </div>
            )}
          </FeaturesSection>
          <hr />
        </>
      ) : null}

      <FeaturesSection title="RFPs">
        {rfpFeatures.map((item, index) => {
          if (!item.visible) return null;
          return (
            <div
              key={`${index}_rfp_features`}
              className="col col-w-full md:col-w-1/3"
            >
              <Field name={item.name} label={item.label} component={Toggle} />
            </div>
          );
        })}
      </FeaturesSection>

      <hr />

      <FeaturesSection title="Reports">
        {reportsAccess.map((item, index) => {
          if (!item.visible) return null;
          return (
            <div
              key={`${index}_reports_features`}
              className="col col-w-full md:col-w-1/3"
            >
              <Field name={item.name} label={item.label} component={Toggle} />
            </div>
          );
        })}
      </FeaturesSection>

      <ButtonPrimary type="submit">
        {submitting ? <Loading inline /> : "Save"}
      </ButtonPrimary>
    </form>
  );
}

const mapStateToProps = state => {
  const { agencyFeatures } = state;
  const childAgencies =
    agencyFeatures?.agencyFeatures?.childAgencies?.map(item => ({
      label: item.name || item.label,
      value: item.id || item.value,
    })) || [];

  return {
    ...state.agency,
    ...state.agencyList,
    isPlatformAdmin: isRolePlatformAdmin(state.auth),
    isRoleEdgeAdmin: isRoleEdgeAdmin(state.auth),
    userAgencyFeatures: userAgencyFeaturesObject(state),
    userAgency: userAgencyObject(state),
    initialValues: {
      ...agencyFeatures.agencyFeatures,
      child_agencies: childAgencies,
    },
  };
};

export default connect(
  mapStateToProps,
  null
)(
  reduxForm({
    form: "agencyFeaturesForm",
    enableReinitialize: true,
  })(AgencyFeaturesForm)
);
